import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, Clock, DollarSign, Sparkles, HeartPulse, UserCheck, Tag, Heart } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { Reveal } from "../components/Reveal";
import { BeforeAfterSlider } from "../components/BeforeAfterSlider";
import { useBooking } from "../context/BookingContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { openBooking } = useBooking();
  const { user, refreshUser } = useAuth();
  const [service, setService] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [results, setResults] = useState([]);

  const isFav = !!(user && user.favorites && user.favorites.includes(slug));
  const toggleFav = async () => {
    if (!user) { toast("Log in to save treatments to your account"); return; }
    try {
      if (isFav) await api.delete(`/member/favorites/${slug}`);
      else await api.post(`/member/favorites`, { slug });
      await refreshUser();
      toast.success(isFav ? "Removed from saved" : "Saved to your favorites");
    } catch {
      toast.error("Could not update saved treatments");
    }
  };

  useEffect(() => {
    setService(null);
    setNotFound(false);
    setResults([]);
    api
      .get(`/services/${slug}`)
      .then((r) => {
        setService(r.data);
        if (r.data?.category) {
          api.get(`/gallery?treatment=${r.data.category}`).then((g) => setResults(g.data)).catch(() => {});
        }
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="pt-44 pb-24 text-center" data-testid="service-not-found">
        <p className="text-[#5C6664]">Treatment not found.</p>
        <Link to="/services" className="text-iri-mid underline mt-3 inline-block">Back to services</Link>
      </div>
    );
  }

  if (!service) {
    return <div className="pt-44 pb-24 text-center text-[#8A9492]">Loading…</div>;
  }

  return (
    <div data-testid="service-detail-page">
      <section className="pt-32 md:pt-40 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-sm text-[#5C6664] hover:text-ink mb-10"
            data-testid="back-to-services"
          >
            <ArrowLeft className="h-4 w-4" /> All services
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <Reveal>
              <div className="img-zoom rounded-[2rem] overflow-hidden aspect-square shadow-2xl shadow-blue-900/10">
                <img src={service.image} alt={service.name} className="w-full h-full object-cover" />
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <span className="text-xs uppercase tracking-[0.2em] text-[#5C6664]">
                {service.category.replace(/-/g, " ")}
              </span>
              <h1 className="mt-3 font-heading text-3xl md:text-5xl font-light tracking-tight text-ink">
                {service.name}
              </h1>

              <div className="flex flex-wrap gap-6 mt-6">
                <span className="inline-flex items-center gap-2 text-ink font-medium">
                  <DollarSign className="h-4 w-4 text-iri-mid" /> {service.price}
                </span>
                <span className="inline-flex items-center gap-2 text-ink">
                  <Clock className="h-4 w-4 text-iri-mid" /> {service.duration}
                </span>
                {service.member_savings && (
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-ink iri-gradient px-3 py-1 rounded-full" data-testid="detail-savings-badge">
                    <Sparkles className="h-3.5 w-3.5" /> {service.member_savings}
                  </span>
                )}
              </div>

              <p className="mt-7 text-[#5C6664] leading-relaxed text-base md:text-lg font-light">
                {service.description}
              </p>

              {service.downtime && (
                <div className="mt-6 flex items-start gap-2.5 rounded-xl bg-stone px-4 py-3 text-sm text-[#5C6664]" data-testid="service-downtime">
                  <Clock className="h-4 w-4 text-iri-mid mt-0.5 shrink-0" />
                  <span><strong className="text-ink">Downtime:</strong> {service.downtime}</span>
                </div>
              )}

              {service.benefits?.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-heading text-lg font-medium text-ink mb-4">Benefits</h3>
                  <ul className="grid sm:grid-cols-2 gap-3">
                    {service.benefits.map((b) => (
                      <li key={b} className="flex items-center gap-3 text-[#5C6664]">
                        <span className="h-6 w-6 rounded-full iri-gradient flex items-center justify-center shrink-0">
                          <Check className="h-3.5 w-3.5 text-ink" />
                        </span>
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-10 flex flex-wrap gap-4">
                <Button
                  data-testid="service-book-button"
                  onClick={() => openBooking(service.name)}
                  className="iri-gradient text-ink font-medium rounded-full h-12 px-8 hover:scale-105 transition-transform"
                >
                  Book this treatment
                </Button>
                <Link to="/contact">
                  <Button variant="outline" className="rounded-full h-12 px-8 border-ink/20 text-ink">
                    Ask a question
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  onClick={toggleFav}
                  data-testid="save-treatment-button"
                  className={`rounded-full h-12 px-6 border-ink/20 gap-2 ${isFav ? "text-iri-mid" : "text-ink"}`}
                >
                  <Heart className={`h-4 w-4 ${isFav ? "fill-iri-mid text-iri-mid" : ""}`} />
                  {isFav ? "Saved" : "Save"}
                </Button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* DETAILS */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-6">
          {service.what_to_expect?.length > 0 && (
            <Reveal className="bg-white rounded-2xl p-8 border border-slate-100" data-testid="what-to-expect">
              <div className="flex items-center gap-2.5 mb-5">
                <Sparkles className="h-5 w-5 text-iri-mid" strokeWidth={1.5} />
                <h2 className="font-heading text-xl font-medium text-ink">What to expect</h2>
              </div>
              <ol className="space-y-4">
                {service.what_to_expect.map((step, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="h-7 w-7 rounded-full iri-gradient flex items-center justify-center shrink-0 text-sm font-medium text-ink">
                      {i + 1}
                    </span>
                    <p className="text-[#5C6664] leading-relaxed pt-0.5">{step}</p>
                  </li>
                ))}
              </ol>
            </Reveal>
          )}

          <div className="space-y-6">
            {service.ideal_candidate?.length > 0 && (
              <Reveal delay={0.05} className="bg-white rounded-2xl p-8 border border-slate-100" data-testid="ideal-candidate">
                <div className="flex items-center gap-2.5 mb-5">
                  <UserCheck className="h-5 w-5 text-iri-mid" strokeWidth={1.5} />
                  <h2 className="font-heading text-xl font-medium text-ink">Ideal candidate</h2>
                </div>
                <ul className="space-y-3">
                  {service.ideal_candidate.map((c, i) => (
                    <li key={i} className="flex items-start gap-3 text-[#5C6664]">
                      <Check className="h-4 w-4 mt-1 text-iri-mid shrink-0" /> {c}
                    </li>
                  ))}
                </ul>
              </Reveal>
            )}

            {service.aftercare?.length > 0 && (
              <Reveal delay={0.1} className="bg-white rounded-2xl p-8 border border-slate-100" data-testid="aftercare">
                <div className="flex items-center gap-2.5 mb-5">
                  <HeartPulse className="h-5 w-5 text-iri-mid" strokeWidth={1.5} />
                  <h2 className="font-heading text-xl font-medium text-ink">Aftercare &amp; recovery</h2>
                </div>
                <ul className="space-y-3">
                  {service.aftercare.map((a, i) => (
                    <li key={i} className="flex items-start gap-3 text-[#5C6664]">
                      <span className="h-1.5 w-1.5 rounded-full iri-gradient mt-2.5 shrink-0" /> {a}
                    </li>
                  ))}
                </ul>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      {/* PRICING TIERS */}
      {service.pricing_tiers?.length > 0 && (
        <section className="pb-8">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal>
              <div className="flex items-center gap-2.5 mb-6">
                <Tag className="h-5 w-5 text-iri-mid" strokeWidth={1.5} />
                <h2 className="font-heading text-2xl font-light text-ink">Pricing &amp; packages</h2>
              </div>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {service.pricing_tiers.map((p, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <div className="bg-white rounded-2xl p-7 border border-slate-100 hover-lift h-full" data-testid={`pricing-tier-${i}`}>
                    <p className="text-sm text-[#8A9492]">{p.name}</p>
                    <p className="font-heading text-3xl font-light text-ink mt-1">{p.price}</p>
                    <p className="text-sm text-[#5C6664] mt-3 leading-relaxed">{p.note}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <p className="text-xs text-[#8A9492] mt-4">
              Final pricing is confirmed at your consultation. VIP members receive additional savings.
            </p>
          </div>
        </section>
      )}

      {/* REAL RESULTS */}
      {results.length > 0 && (
        <section className="pb-8">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal>
              <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
                <div className="flex items-center gap-2.5">
                  <Sparkles className="h-5 w-5 text-iri-mid" strokeWidth={1.5} />
                  <h2 className="font-heading text-2xl font-light text-ink">Real results</h2>
                </div>
                <Link to="/gallery" className="text-sm text-iri-mid underline" data-testid="service-view-gallery">View full gallery →</Link>
              </div>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {results.slice(0, 3).map((it) => (
                <Reveal key={it.id}>
                  <div className="bg-white rounded-2xl overflow-hidden border border-slate-100" data-testid={`service-result-${it.id}`}>
                    <BeforeAfterSlider before={it.before_url} after={it.after_url} alt={it.title || service.name} className="aspect-square" />
                    {it.title && <p className="text-xs text-[#5C6664] p-3">{it.title}</p>}
                  </div>
                </Reveal>
              ))}
            </div>
            <p className="text-xs text-[#8A9492] mt-4">Individual results vary.</p>
          </div>
        </section>
      )}

      {/* FAQs */}
      {service.faqs?.length > 0 && (
        <section className="pb-24">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <Reveal>
              <h2 className="font-heading text-2xl md:text-3xl font-light text-ink mb-6" data-testid="service-faqs-heading">
                Frequently asked questions
              </h2>
              <Accordion type="single" collapsible className="w-full" data-testid="service-faqs">
                {service.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-b border-slate-200">
                    <AccordionTrigger className="text-left font-heading text-ink font-medium hover:no-underline" data-testid={`faq-trigger-${i}`}>
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#5C6664] leading-relaxed">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </section>
      )}
    </div>
  );
}
